import { useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { fetchUserRoles, isAdminUser, isSuperAdminUser } from './auth-utils';

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const rolesLoadedForUser = useRef<string | null>(null);
  // Bumped on every auth event; a background updateUserRoles() call captures
  // its value and checks it against this ref before applying results, so an
  // older, slower-resolving call can't stomp state set by a newer one (e.g.
  // rapid sign-out/sign-in cycles). Optional on updateUserRoles so the
  // separate deferred call in useAuthActions.ts is unaffected.
  const authEventGeneration = useRef(0);

  const updateUserRoles = async (userId: string, isStale?: () => boolean) => {
    try {
      const roles = await fetchUserRoles(userId);
      let adminStatus = isAdminUser(roles);
      let superAdminStatus = isSuperAdminUser(roles);

      try {
        const [isAdminRpc, isSuperAdminRpc] = await Promise.allSettled([
          supabase.rpc('is_admin_user', { user_id: userId }),
          supabase.rpc('check_is_super_admin', { _user_id: userId })
        ]);
        if (isAdminRpc.status === 'fulfilled' && isAdminRpc.value.data !== null && !isAdminRpc.value.error) {
          adminStatus = Boolean(isAdminRpc.value.data);
        }
        if (isSuperAdminRpc.status === 'fulfilled' && isSuperAdminRpc.value.data !== null && !isSuperAdminRpc.value.error) {
          superAdminStatus = Boolean(isSuperAdminRpc.value.data);
        }
      } catch {
        // fall back to client-side check — already set above
      }

      if (isStale?.()) {
        return { isAdmin: adminStatus, isSuperAdmin: superAdminStatus };
      }

      setUserRoles(roles);
      setIsAdmin(adminStatus);
      setIsSuperAdmin(superAdminStatus);
      rolesLoadedForUser.current = userId;
      return { isAdmin: adminStatus, isSuperAdmin: superAdminStatus };
    } catch (error) {
      console.error('Error updating user roles:', error);
      if (isStale?.()) {
        return { isAdmin: false, isSuperAdmin: false };
      }
      setUserRoles([]);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return { isAdmin: false, isSuperAdmin: false };
    }
  };

  useEffect(() => {
    let mounted = true;

    // Single source of truth — onAuthStateChange handles everything
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        const myGeneration = ++authEventGeneration.current;

        console.log('🔐 Auth event:', event, session?.user?.email);

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // TOKEN_REFRESHED: roles already loaded for this user — skip reload
          if (
            event === 'TOKEN_REFRESHED' &&
            rolesLoadedForUser.current === session.user.id
          ) {
            setLoading(false);
            return;
          }
          // Only load roles if not already loaded for this user
          if (rolesLoadedForUser.current !== session.user.id) {
            setLoading(true);
            // Deliberately NOT awaited: supabase-js's signInWithPassword()
            // awaits every onAuthStateChange subscriber's callback before its
            // own promise resolves (_notifyAllSubscribers -> Promise.all), so
            // awaiting this DB round-trip here was transitively blocking
            // sign-in itself — the actual cause of the 30s-timeout / false
            // "Login Failed" bug. Firing it in the background instead lets
            // this callback return immediately; setLoading(false) still only
            // fires once roles actually resolve, so anything gating on
            // `loading` (e.g. Admin.tsx's access check) sees no change in
            // timing. The isStale check discards a result superseded by a
            // newer auth event (e.g. rapid sign-out/sign-in).
            updateUserRoles(
              session.user.id,
              () => authEventGeneration.current !== myGeneration
            )
              .catch((err) => {
                console.error('🔐 Error updating user roles (background):', err);
              })
              .finally(() => {
                if (mounted && authEventGeneration.current === myGeneration) {
                  setLoading(false);
                }
              });
          } else {
            setLoading(false);
          }
        } else {
          setUserRoles([]);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          rolesLoadedForUser.current = null;
          setLoading(false);
        }
      }
    );

    // Trigger initial session load
    supabase.auth.getSession().then(({ data: { session } }) => {
      // onAuthStateChange will handle it via INITIAL_SESSION event
      // Safety net if INITIAL_SESSION doesn't fire
      if (mounted && session === null) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    session,
    loading,
    userRoles,
    isAdmin,
    isSuperAdmin,
    updateUserRoles
  };
};
